function normalizedAmount(product) {
    return Number(product.amount || 0) * Number(product.amountUnit && product.amountUnit.multiplier ? product.amountUnit.multiplier : 1)
}

function normalizedUnitName(product) {
    const baseUnit = product.units && product.units.find((unit) => Number(unit.multiplier) === 1)

    return baseUnit ? baseUnit.name : product.amountUnit.name
}

function isInRuleScope(product, rule, category) {
    if (!rule) return false
    if (rule.wholeGroup === true) return true

    const categoryIds = rule.categoryIds || []
    const productCategoryId = product.category_id
    const activeCategoryId = category && category.id !== undefined ? category.id : null

    return (productCategoryId != null && categoryIds.map(Number).includes(Number(productCategoryId))) ||
        (activeCategoryId != null && categoryIds.map(Number).includes(Number(activeCategoryId)))
}

function isBelowPalletThreshold(product) {
    return product.pallet !== null &&
        product.pallet.calculateAbove !== null &&
        normalizedAmount(product) < Number(product.pallet.calculateAbove)
}

function isWeightFallbackProduct(product, rule, category) {
    return Number(product.amount || 0) > 0 &&
        product.weight != null &&
        Number(product.weight) > 0 &&
        isInRuleScope(product, rule, category) &&
        (product.pallet === null || isBelowPalletThreshold(product))
}

function normalPalletContribution(product) {
    if (Number(product.amount || 0) <= 0 || product.pallet === null) return 0

    const amount = normalizedAmount(product)
    if (product.pallet.calculateAbove !== null && amount < Number(product.pallet.calculateAbove)) return 0

    // A product can carry a pallet_id without a pallet unit row, leaving divider null.
    // Backend Product::amountOfPallet() counts 0 pallets for those; match it instead of dividing by zero.
    const divider = Number(product.pallet.divider)
    if (!(divider > 0)) return 0

    const fraction = amount / divider

    return product.pallet.needRound ? Math.ceil(fraction) : fraction
}

function fractionRemainderCandidate(product, rule, category) {
    if (!rule || rule.enabled !== true) return null
    if (Number(product.amount || 0) <= 0 || product.pallet === null) return null
    if (!isInRuleScope(product, rule, category)) return null
    if (isBelowPalletThreshold(product)) return null

    const amount = normalizedAmount(product)
    const divider = Number(product.pallet.divider)
    if (divider <= 0) return null

    const fraction = amount / divider
    const fullPallets = Math.floor(fraction)
    if (fullPallets < 1 || fraction === fullPallets) return null

    const fractionalAmount = amount - (fullPallets * divider)
    const fractionalWeight = fractionalAmount * Number(product.weight || 0)
    if (fractionalWeight <= 0) return null

    return {
        product,
        fraction,
        fullPallets,
        normalContribution: normalPalletContribution(product),
        fractionalWeight,
    }
}

function palletNameFor(product, pallets) {
    if (product.pallet === null) return null

    const palletEntry = pallets.find((pallet) => Number(pallet.id) === Number(product.pallet.type))

    return palletEntry ? palletEntry.name : product.pallet.type
}

function palletDescriptionFor(product, pallets) {
    if (product.pallet === null) return null

    const palletEntry = pallets.find((pallet) => Number(pallet.id) === Number(product.pallet.type))

    return palletEntry ? (palletEntry.label || palletEntry.name) : product.pallet.type
}

function normalRowFor(product, pallets, contributionMeta = null) {
    const amount = normalizedAmount(product)
    const belowThreshold = isBelowPalletThreshold(product)
    const divider = Number(product.pallet.divider)
    const hasDivider = divider > 0
    const fraction = hasDivider ? amount / divider : 0
    const contribution = normalPalletContribution(product)
    const hasWeight = product.weight != null && Number(product.weight) > 0
    const remainderAmount = hasDivider ? amount - (Math.floor(fraction) * divider) : 0
    const defaultRuleMeta = belowThreshold ? {
        ruleCode: 'below_threshold',
        ruleLabel: 'Küszöb alatt',
        ruleReason: 'A normál raklap számítás 0, mert a mennyiség nem érte el a minimum küszöböt.',
    } : {
        ruleCode: 'normal',
        ruleLabel: 'Normál',
        ruleReason: 'Normál raklap számítás.',
    }
    const ruleMeta = contributionMeta || defaultRuleMeta

    return {
        id: product.id,
        name: product.name,
        palletName: palletNameFor(product, pallets),
        palletDescription: palletDescriptionFor(product, pallets),
        amount: product.amount,
        unitName: product.amountUnit.name,
        normalizedAmount: amount,
        normalizedUnitName: normalizedUnitName(product),
        pallet: product.pallet,
        weight: hasWeight ? Number(product.weight) : null,
        belowThreshold,
        fraction: fraction.toFixed(4),
        ruleCode: ruleMeta.ruleCode,
        ruleLabel: ruleMeta.ruleLabel,
        ruleReason: ruleMeta.ruleReason,
        fractionalWeight: ruleMeta.fractionalWeight || null,
        totalFractionalWeight: ruleMeta.totalFractionalWeight || null,
        contribution: contributionMeta ? ruleMeta.contribution : (product.pallet.needRound ? contribution : parseFloat(contribution.toFixed(4))),
        extraWeight: !belowThreshold && hasWeight ? remainderAmount * Number(product.weight) : null,
        totalWeight: hasWeight ? amount * Number(product.weight) : null,
    }
}

function weightRowFor(product) {
    const amount = normalizedAmount(product)
    const belowThreshold = isBelowPalletThreshold(product)

    return {
        id: product.id,
        name: product.name,
        amount: product.amount,
        unitName: product.amountUnit.name,
        normalizedAmount: amount,
        normalizedUnitName: normalizedUnitName(product),
        weight: Number(product.weight),
        totalWeight: amount * Number(product.weight),
        ruleCode: belowThreshold ? 'threshold_weight_fallback' : 'weight_fallback',
        ruleLabel: belowThreshold ? 'Küszöb alatt -> súly' : 'Súly',
        ruleReason: belowThreshold
            ? 'A normál raklap számítás küszöb alatt 0, ezért a termék teljes súlya az alsó szabályba kerül.'
            : 'A terméknek nincs normál raklap számítása, ezért a teljes súlya az alsó szabályba kerül.',
    }
}

export function calculatePalletData(categories, pallets, specialPalletRules) {
    const weightFallbackRule = specialPalletRules && specialPalletRules.weightFallbackRule
    const fractionRemainderRule = specialPalletRules && specialPalletRules.fractionRemainderRule
    const amounts = {}
    const normalRows = []
    const weightRows = []
    const fractionCandidates = []
    const safeCategories = categories || []

    pallets.forEach((pallet) => {
        amounts[pallet.id] = 0
    })

    safeCategories.forEach((category) => {
        const products = category.products || []

        products.forEach((product) => {
            if (Number(product.amount || 0) <= 0) {
                return
            }

            if (product.pallet !== null) {
                const fractionCandidate = fractionRemainderCandidate(product, fractionRemainderRule, category)

                if (fractionCandidate) {
                    fractionCandidates.push(fractionCandidate)
                    return
                }

                const contribution = normalPalletContribution(product)

                if (contribution > 0) {
                    amounts[product.pallet.type] += contribution
                }

                normalRows.push(normalRowFor(product, pallets))
            }

            if (isWeightFallbackProduct(product, weightFallbackRule, category)) {
                weightRows.push(weightRowFor(product))
            }
        })
    })

    const totalFractionalWeight = fractionCandidates.reduce((sum, row) => sum + row.fractionalWeight, 0)
    const maxFractionWeight = Number(fractionRemainderRule && fractionRemainderRule.maxWeightExclusive ? fractionRemainderRule.maxWeightExclusive : 25)
    const roundFractionDown = fractionCandidates.length > 0 && totalFractionalWeight < maxFractionWeight

    fractionCandidates.forEach((candidate) => {
        const contribution = roundFractionDown ? candidate.fullPallets : candidate.normalContribution

        amounts[candidate.product.pallet.type] += contribution
        normalRows.push(normalRowFor(candidate.product, pallets, {
            ruleCode: roundFractionDown ? 'fraction_remainder' : 'normal',
            ruleLabel: roundFractionDown ? 'Tört < 25 kg' : 'Normál',
            ruleReason: roundFractionDown
                ? 'Az összes tört raklap maradék 25 kg alatt van, ezért meglévő raklapra kerül.'
                : 'Az összes tört raklap maradék elérte a küszöböt, ezért marad a normál kerekítés.',
            contribution,
            fractionalWeight: candidate.fractionalWeight,
            totalFractionalWeight,
        }))
    })

    const weightTotal = weightRows.reduce((sum, row) => sum + row.totalWeight, 0)

    if (
        weightFallbackRule &&
        amounts[weightFallbackRule.palletId] !== undefined &&
        weightTotal >= Number(weightFallbackRule.minWeight)
    ) {
        amounts[weightFallbackRule.palletId] += Number(weightFallbackRule.maxPallets)
    }

    return {
        amounts,
        normalRows,
        weightRows,
        weightTotal,
        weightRuleActive: !!weightFallbackRule && weightTotal >= Number(weightFallbackRule.minWeight),
        weightFallbackRule,
        fractionRemainderTotalWeight: totalFractionalWeight,
    }
}
